import React, { Fragment, ReactNode } from 'react';
import { ContentHeadline } from '../../ContentHeadline';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import ILink from '../../../../types/ILink';
import { PageMembershipStatus } from '../../../../types/api/PageMembershipStatus';
import TextBubble from '../../TextBubble/TextBubble';
import { UserMembershipRole } from '../UserMembershipTypes';
import { DL, DT, DD } from '../../DefinitionList';
import { HeadingHierarchy } from '../../Heading/Heading';
import { DefinitionListModifier } from '../../DefinitionList/DefinitionList.utils';

type UserMembershipProps<T> = {
  status: PageMembershipStatus;
  roles: T[];
  pageLink: ILink;
  hierarchy?: HeadingHierarchy;
  children?: (role: T) => ReactNode;
};

export function UserMembership<T extends UserMembershipRole>({
  status,
  pageLink,
  roles,
  hierarchy = 3,
  children,
}: UserMembershipProps<T>) {
  const { t } = useSonTranslation('UserMemberships');
  const statusIsNotActive = status && status !== 'active';
  const roleTranslation = roles.length > 1 ? t('role_plural') : t('role');

  return (
    <article className="user-membership">
      <ContentHeadline url={pageLink.url} title={pageLink.name} hierarchy={hierarchy} />
      {statusIsNotActive && (
        <p className="user-membership__status">
          <dfn className="user-membership__status-title">{t('statusTitle')}: </dfn>
          {t(`status.${status}`)}
        </p>
      )}

      {roles?.length > 0 && (
        <DL>
          <DT>{roleTranslation}</DT>
          {roles?.map(role => (
            <Fragment key={role.name}>
              <DD modifier={DefinitionListModifier.MULTIPLE}>
                <div className="user-membership__container">
                  <div className="user-membership__role-name">{role.name}</div>
                  <div className="user-membership__context-menu">{children && children(role)}</div>
                </div>
              </DD>

              {role.note && (
                <DD modifier={DefinitionListModifier.MULTIPLE}>
                  <div className="user-membership__content-note">
                    <TextBubble type={t('note')} content={role.note} />
                  </div>
                </DD>
              )}
            </Fragment>
          ))}
        </DL>
      )}
    </article>
  );
}
